import Papa from 'papaparse';

// The published Google Sheet (File > Share > Publish to web > Entire document).
// We reuse the same publish link as before but pull EVERY tab, not just the default one,
// by appending each tab's gid to the same published document.
const GOOGLE_SHEET_CSV_URL = process.env.REACT_APP_GOOGLE_SHEET_CSV_URL || "";

const PUB_ID_MATCH = GOOGLE_SHEET_CSV_URL.match(/\/d\/e\/([^/]+)\//);
const PUB_ID = PUB_ID_MATCH ? PUB_ID_MATCH[1] : "";

// Tab name -> gid, read once from the published sheet's tab list.
// If tabs are renamed/added/removed in the source spreadsheet, update this map
// (open the sheet's "pubhtml" link and look for name/gid pairs in the page source).
//
// Refreshed 2026-09-12: the sheet owner deleted/recreated every tab again (this has now happened
// three times — see [[casagrand-sheet-quirks]] memory), which changes every gid at once and made
// every tab 400. Re-derived from the pub /pubhtml page's embedded name/gid metadata and verified
// each returns real CSV before updating. "Grade 10 Overview" keeps carrying the ACADEMICS-shaped
// content (title/KEY METRIC/Section-Campus), so it stays mapped to the `academics` key.
export const SHEET_GIDS = {
  month: { name: "MONTH", gid: "1707445734" },
  mainDashboard: { name: "MAIN DASHBOARD", gid: "43998752" },
  perumbakkam: { name: "PERUMBAKKAM", gid: "2004283174" },
  kvr: { name: "KVR", gid: "1483319105" },
  thiruvanmiyur: { name: "THIRUVANMIYUR", gid: "1249447456" },
  thirumudivakkam: { name: "THIRUMUDIVAKKAM", gid: "1617646686" },
  hyderabad: { name: "HYDERABAD", gid: "1057203009" },
  transport: { name: "TRANSPORT", gid: "352713114" },
  humanResources: { name: "HUMAN RESOURCES", gid: "429214937" },
  bestSchool: { name: "BEST SCHOOL INITIATIVE", gid: "239816778" },
  academics: { name: "Grade 10 Overview", gid: "225090022" },
  toppers: { name: "Toppers & Support Needed", gid: "78833847" },
};

const buildTabUrl = (gid) =>
  `https://docs.google.com/spreadsheets/d/e/${PUB_ID}/pub?gid=${gid}&single=true&output=csv`;

const parseCsvGrid = (url) =>
  new Promise((resolve) => {
    Papa.parse(url, {
      download: true,
      header: false,
      skipEmptyLines: false,
      complete: (results) => resolve(results.data || []),
      error: (error) => {
        console.error(`Failed to fetch/parse sheet tab: ${url}`, error);
        resolve([]);
      },
    });
  });

const fetchAllTabs = async () => {
  const keys = Object.keys(SHEET_GIDS);
  const grids = await Promise.all(
    keys.map((key) => parseCsvGrid(buildTabUrl(SHEET_GIDS[key].gid)))
  );
  return Object.fromEntries(keys.map((key, i) => [key, grids[i]]));
};

// ---------------------------------------------------------------------------
// Cell / row helpers
// ---------------------------------------------------------------------------

const EXCEL_ERROR_RE = /^#[A-Z0-9/]+[!?]?$/;

const isBlank = (value) => value === undefined || value === null || String(value).trim() === "";

// Cleans a raw cell: trims text and blanks out Excel error values ("#REF!", "#N/A", ...).
// A literal "-" is left as-is — it's a real placeholder value in some sheets (e.g. "no date
// scheduled yet") and should display as-is, not disappear. Numeric parsing (toNumber) already
// falls back to 0 for it regardless, so this doesn't affect any numeric field.
const cleanCell = (value) => {
  if (isBlank(value)) return "";
  const str = String(value).trim();
  if (EXCEL_ERROR_RE.test(str)) return "";
  return typeof value === "string" ? str : value;
};

const firstNonEmptyIndex = (row = [], from = 0) => {
  for (let i = from; i < row.length; i += 1) {
    if (!isBlank(cleanCell(row[i]))) return i;
  }
  return -1;
};

// Finds the first "label" cell in a row and the next non-empty cell after it as its value.
// Works regardless of which column a row's label starts in (some tabs are shifted by a column).
const getLabelValue = (row = []) => {
  const labelIdx = firstNonEmptyIndex(row, 0);
  if (labelIdx === -1) return { label: "", value: "" };
  const valueIdx = firstNonEmptyIndex(row, labelIdx + 1);
  return {
    label: String(cleanCell(row[labelIdx])).trim(),
    value: valueIdx === -1 ? "" : cleanCell(row[valueIdx]),
  };
};

const toNumber = (value) => {
  const cleaned = cleanCell(value);
  if (isBlank(cleaned)) return 0;
  const n = Number(String(cleaned).replace(/[%,]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const rowLabel = (row = []) => getLabelValue(row).label;

const findRowIndex = (rows, predicate, from = 0) => {
  for (let i = from; i < rows.length; i += 1) {
    if (predicate(rows[i], i)) return i;
  }
  return -1;
};

// Finds a header row's column index for a given column title (case-insensitive, trims whitespace).
const headerColIndex = (headerRow = [], title) =>
  headerRow.findIndex((c) => String(c ?? "").trim().toLowerCase() === title.toLowerCase());

// All column indexes matching a title — for header rows where two side-by-side tables reuse the
// same column name (e.g. two "Meeting" columns), so the caller can pick the right one instead of
// always getting the first.
const headerColIndexes = (headerRow = [], title) => {
  const target = title.toLowerCase();
  const indexes = [];
  headerRow.forEach((c, i) => {
    if (String(c ?? "").trim().toLowerCase() === target) indexes.push(i);
  });
  return indexes;
};

// ---------------------------------------------------------------------------
// MAIN DASHBOARD tab
// ---------------------------------------------------------------------------

const parseMainDashboardSheet = (rows) => {
  const gradeSectionIdx = findRowIndex(rows, (r) => rowLabel(r) === "Total Student Strength Grade wise");
  const statRows = gradeSectionIdx === -1 ? rows : rows.slice(0, gradeSectionIdx);

  const dashboardTotalModel = statRows
    .map(getLabelValue)
    .filter(({ label, value }) => label && !isBlank(value))
    .map(({ label, value }, index) => ({ id: index + 1, total: label, count: value }));

  // Grade-wise student strength table (fixed columns, read from its own header row).
  const studentStrengthGradeWiseData = [];
  const gradeHeaderIdx = gradeSectionIdx === -1
    ? -1
    : findRowIndex(rows, (r) => rowLabel(r) === "Grade", gradeSectionIdx + 1);

  if (gradeHeaderIdx !== -1) {
    const headerRow = rows[gradeHeaderIdx];
    const cols = {
      grade: headerColIndex(headerRow, "Grade"),
      perumbakkam: headerColIndex(headerRow, "Perumbakkam"),
      KVR: headerColIndex(headerRow, "KVR"),
      thiruvanmiyur: headerColIndex(headerRow, "Thiruvanmiyur"),
      thirumudivakkam: headerColIndex(headerRow, "Thirumudivakkam"),
      hyderbad: headerColIndex(headerRow, "Hyderabad"),
      total: headerColIndex(headerRow, "Total"),
    };

    for (let i = gradeHeaderIdx + 1; i < rows.length; i += 1) {
      const row = rows[i];
      const grade = String(cleanCell(row[cols.grade]) || "").trim();
      if (!grade) break;

      const isTotalRow = grade.toUpperCase() === "CAMPUS TOTAL";
      studentStrengthGradeWiseData.push({
        grade: isTotalRow ? "Campus Total" : grade,
        perumbakkam: toNumber(row[cols.perumbakkam]),
        KVR: toNumber(row[cols.KVR]),
        thiruvanmiyur: toNumber(row[cols.thiruvanmiyur]),
        thirumudivakkam: toNumber(row[cols.thirumudivakkam]),
        hyderbad: toNumber(row[cols.hyderbad]),
        total: toNumber(row[cols.total]),
      });

      if (isTotalRow) break;
    }
  }

  // "Total Supply" table -> admissions pipeline (campus, supply, AY figures, overall).
  const admissionsPipelineData = [];
  const supplySectionIdx = findRowIndex(rows, (r) => rowLabel(r) === "Total Supply");
  const supplyTableHeaderIdx = supplySectionIdx === -1
    ? -1
    : findRowIndex(rows, (r) => rowLabel(r) === "Campus", supplySectionIdx + 1);

  const CAMPUS_NAMES = { PRM: "Perumbakkam", KVR: "KVR", THV: "Thiruvanmiyur", TMV: "Thirumudivakkam", GPD: "Hyderabad", OVERALL: "OVERALL" };

  if (supplyTableHeaderIdx !== -1) {
    const headerRow = rows[supplyTableHeaderIdx];
    const cols = {
      campus: headerColIndex(headerRow, "Campus"),
      supply: headerColIndex(headerRow, "Total Supply"),
      ay1: headerColIndex(headerRow, "AY 26–27"),
      ay2: headerColIndex(headerRow, "AY 27–28"),
      overall: headerColIndex(headerRow, "Overall Admission"),
    };

    for (let i = supplyTableHeaderIdx + 1; i < rows.length; i += 1) {
      const row = rows[i];
      const code = String(cleanCell(row[cols.campus]) || "").trim();
      if (!code) break;

      const isOverall = code.toUpperCase() === "OVERALL";
      admissionsPipelineData.push({
        campus: isOverall ? "OVERALL" : (CAMPUS_NAMES[code.toUpperCase()] || code),
        supply: toNumber(row[cols.supply]),
        AY20252026: toNumber(row[cols.ay1]),
        AY20262027: toNumber(row[cols.ay2]),
        overall: toNumber(row[cols.overall]),
      });

      if (isOverall) break;
    }
  }

  // "<Month> Campus Activities" table (the section title names the current month,
  // so match on the fixed "Campus Activities" suffix rather than a literal month name).
  const campusActivitiesData = [];
  const activitiesSectionIdx = findRowIndex(rows, (r) => /campus activities$/i.test(rowLabel(r)));
  const campusActivitiesTitle = activitiesSectionIdx === -1 ? "" : rowLabel(rows[activitiesSectionIdx]);
  const activitiesHeaderIdx = activitiesSectionIdx === -1
    ? -1
    : findRowIndex(rows, (r) => rowLabel(r) === "Event", activitiesSectionIdx + 1);

  if (activitiesHeaderIdx !== -1) {
    for (let i = activitiesHeaderIdx + 1; i < rows.length; i += 1) {
      const { label } = getLabelValue(rows[i]);
      if (!label) continue;
      const row = rows[i];
      const eventIdx = firstNonEmptyIndex(row, 0);
      const dateIdx = eventIdx === -1 ? -1 : firstNonEmptyIndex(row, eventIdx + 1);
      const timeIdx = dateIdx === -1 ? -1 : firstNonEmptyIndex(row, dateIdx + 1);
      campusActivitiesData.push({
        events: label,
        date: dateIdx === -1 ? "" : cleanCell(row[dateIdx]),
        time: timeIdx === -1 ? "" : cleanCell(row[timeIdx]),
      });
    }
  }

  return { dashboardTotalModel, studentStrengthGradeWiseData, admissionsPipelineData, campusActivitiesData, campusActivitiesTitle };
};

// ---------------------------------------------------------------------------
// MONTH tab: a single cell naming the current reporting month (e.g. "June 2026")
// ---------------------------------------------------------------------------

const parseMonthSheet = (rows) => {
  const row = rows.find((r) => firstNonEmptyIndex(r, 0) !== -1);
  return row ? rowLabel(row) : "";
};

// ---------------------------------------------------------------------------
// Single-campus tabs (PERUMBAKKAM, KVR, THIRUVANMIYUR, THIRUMUDIVAKKAM, HYDERABAD)
// ---------------------------------------------------------------------------

const parseCampusSheet = (rows) =>
  rows
    .map(getLabelValue)
    .filter(({ label }) => label && !/campus$/i.test(label))
    .map(({ label, value }) => ({ events: label, summary: value === "" ? "" : value }));

// ---------------------------------------------------------------------------
// TRANSPORT / HUMAN RESOURCES tabs: group totals + per-campus comparison table
// ---------------------------------------------------------------------------

const CAMPUS_BLOCK_LABELS = [
  { match: "perumbakkam", key: "perumbakkam" },
  { match: "kvr", key: "kvr" },
  { match: "thiruvanmiyur", key: "thiruvanmiyur" },
  { match: "thirumudivakkam", key: "thirumudivakkam" },
  { match: "hyderabad", key: "hyderbad" },
];

const normalizeLabel = (value = "") => value.toLowerCase().replace(/\s+/g, " ").trim();

const parseGroupComparisonSheet = (rows) => {
  const blockHeaderIdx = findRowIndex(rows, (row) => {
    const text = row.map((c) => normalizeLabel(String(c ?? ""))).join("|");
    return text.includes("perumbakkam") && text.includes("kvr");
  });

  const groupTotalRows = blockHeaderIdx === -1 ? rows : rows.slice(0, blockHeaderIdx);
  const groupTotals = {};
  groupTotalRows.forEach((row) => {
    const { label, value } = getLabelValue(row);
    if (label && !isBlank(value)) {
      groupTotals[normalizeLabel(label)] = value;
    }
  });

  const output = [];
  if (blockHeaderIdx !== -1) {
    const headerRow = rows[blockHeaderIdx];
    const blocks = CAMPUS_BLOCK_LABELS.map(({ match, key }) => {
      const labelCol = headerRow.findIndex((c) => normalizeLabel(String(c ?? "")).startsWith(match));
      return { key, labelCol, valueCol: labelCol === -1 ? -1 : labelCol + 1 };
    });

    for (let i = blockHeaderIdx + 1; i < rows.length; i += 1) {
      const row = rows[i];
      const anchor = blocks.find((b) => b.labelCol !== -1);
      const category = anchor ? String(cleanCell(row[anchor.labelCol]) || "").trim() : "";
      if (!category) continue;

      const entry = { category };
      let numericSum = 0;
      let hasNumeric = false;
      blocks.forEach(({ key, valueCol }) => {
        const raw = valueCol === -1 ? "" : cleanCell(row[valueCol]);
        entry[key] = raw;
        if (typeof raw === "number" || (typeof raw === "string" && raw && !raw.includes("%") && !Number.isNaN(Number(raw)))) {
          numericSum += toNumber(raw);
          hasNumeric = true;
        }
      });

      const groupTotal = groupTotals[normalizeLabel(category)];
      entry.groupTotal = groupTotal !== undefined ? groupTotal : (hasNumeric ? numericSum : 0);
      output.push(entry);
    }
  }

  // Raw label -> value map from the sheet's standalone summary block (e.g. "Total COAE",
  // "Total Head Office") — metrics with no per-campus breakdown, kept alongside the
  // per-campus rows so pages can read them directly instead of hardcoding a fallback.
  output.groupTotals = groupTotals;

  return output;
};

// ---------------------------------------------------------------------------
// BEST SCHOOL INITIATIVE tab
// ---------------------------------------------------------------------------

const parseBestSchoolSheet = (rows) => {
  const targetDateRow = rows.find((r) => rowLabel(r) === "TARGET COMPLETION DATE");
  const targetDate = targetDateRow ? getLabelValue(targetDateRow).value : "";

  // Top summary block (Ideas Generated / Parameters / Idea Reviewed / Ideas Selected /
  // Completion %) — direct figures the sheet owner maintains by hand, read verbatim rather
  // than derived from the Meeting/Parameter tables below.
  const readTopLevelValue = (label) => {
    const row = rows.find((r) => rowLabel(r) === label);
    return row ? getLabelValue(row).value : "";
  };

  // "Ideas Generated" as its own row is gone from the sheet; "Total Ideas collected" is what's
  // there now and is clearly meant as its replacement — but the sheet owner has flipped between
  // two shapes for that cell across successive refreshes: sometimes a clean label/value pair
  // ("Total Ideas collected" | "16000+"), sometimes one merged free-text cell ("Total Ideas
  // collected - 31000+", where getLabelValue reads the whole phrase as the label and finds no
  // paired value). Handle both rather than re-breaking every time the format flips back.
  const totalIdeasCollectedFallback = () => {
    const clean = readTopLevelValue("Total Ideas collected");
    if (clean) return clean;

    const mergedRow = rows.find((r) => /total ideas collected/i.test(rowLabel(r)));
    const match = mergedRow ? rowLabel(mergedRow).match(/total ideas collected\s*-\s*(.+)$/i) : null;
    return match ? match[1].trim() : "";
  };

  const bestSchoolSummary = {
    ideasGenerated: readTopLevelValue("Ideas Generated") || totalIdeasCollectedFallback(),
    parameters: toNumber(readTopLevelValue("Parameters")),
    ideaReviewed: toNumber(readTopLevelValue("Idea Reviewed")),
    ideasSelected: toNumber(readTopLevelValue("Ideas Selected")),
    completionPercent: readTopLevelValue("Completion %"),
  };

  const GenerationTimelineData = [];
  const IdeasGenerationTargetData = [];
  const ReviewedAndSelectedData = [];
  let IdeasGenerationTargetTotal = null;
  let ReviewedAndSelectedTotal = null;

  // Stage / generation-timeline block and the Meeting reviewed/selected block both use a
  // "Meeting" header label, so they're told apart by their OTHER column names rather than by
  // position — this stays correct whether the sheet lays them out side-by-side or stacked.
  const stageHeaderIdx = findRowIndex(rows, (r) => {
    const text = r.map((c) => normalizeLabel(String(c ?? ""))).join("|");
    return text.includes("meeting") && text.includes("target fixed");
  });

  let ideasTarget = 0;

  if (stageHeaderIdx !== -1) {
    const headerRow = rows[stageHeaderIdx];
    const cols = {
      stage: headerColIndex(headerRow, "Meeting"),
      month: headerColIndex(headerRow, "Month"),
      targetFixed: headerColIndex(headerRow, "Target Fixed"),
      targetAchieved: headerColIndex(headerRow, "Target Achieved"),
      percentAchieved: headerColIndex(headerRow, "% Achieved"),
      overallTargetPercent: headerColIndex(headerRow, "Overall Target %"),
    };

    for (let i = stageHeaderIdx + 1; i < rows.length; i += 1) {
      const row = rows[i];
      const stage = String(cleanCell(row[cols.stage]) || "").trim();
      if (!stage) continue;

      const targetFixed = toNumber(row[cols.targetFixed]);
      const targetAchieved = toNumber(row[cols.targetAchieved]);
      const percentAchieved = toNumber(row[cols.percentAchieved]);
      const overallTargetPercent = toNumber(row[cols.overallTargetPercent]);
      const month = String(cleanCell(row[cols.month]) || "").trim();

      if (normalizeLabel(stage).startsWith("total")) {
        // The Total row's own Target Fixed is the overall ideas target (e.g. 1400) —
        // read it here rather than hardcoding it in the UI.
        ideasTarget = targetFixed;
        IdeasGenerationTargetTotal = {
          stage: "Total",
          month,
          targetFixed,
          targetAchieved,
          percentAchieved,
          overallTargetPercent,
        };
        break;
      }

      IdeasGenerationTargetData.push({
        stage,
        month,
        targetFixed,
        targetAchieved,
        percentAchieved,
        overallTargetPercent,
      });

      const isLast = normalizeLabel(stage) === "stage 5";
      GenerationTimelineData.push({
        stage,
        numberOfIdeas: targetAchieved || targetFixed,
        deadline: isLast && targetDate ? targetDate : month,
        status: targetAchieved > 0 ? "Completed" : "In Progress",
      });
    }
  }

  const meetingHeaderIdx = findRowIndex(rows, (r) => {
    const text = r.map((c) => normalizeLabel(String(c ?? ""))).join("|");
    return text.includes("meeting") && text.includes("ideas reviewed") && text.includes("ideas selected");
  });

  if (meetingHeaderIdx !== -1) {
    const headerRow = rows[meetingHeaderIdx];
    const reviewedCol = headerColIndex(headerRow, "Ideas Reviewed");
    const selectedCol = headerColIndex(headerRow, "Ideas Selected");
    // This block sits beside the Ideas Generation Target block in the same header row, and both
    // reuse the column name "Meeting" — headerColIndex would always grab the OTHER block's
    // "Meeting" column (the first one), so the row would render with "Stage N" labels instead of
    // "Meeting N" even though the numeric columns (unique names) were already correct. Anchor on
    // whichever "Meeting" column sits immediately before this block's own "Ideas Reviewed" column.
    const meetingCandidates = headerColIndexes(headerRow, "Meeting");
    const meetingCol = meetingCandidates.filter((i) => i < reviewedCol).pop() ?? meetingCandidates[0] ?? -1;
    const selectionPercentCol = headerColIndex(headerRow, "Selection %");

    for (let i = meetingHeaderIdx + 1; i < rows.length; i += 1) {
      const row = rows[i];
      const meetingLabel = String(cleanCell(row[meetingCol]) || "").trim();
      if (!meetingLabel) continue;
      if (normalizeLabel(meetingLabel).startsWith("total")) {
        ReviewedAndSelectedTotal = {
          ideas: toNumber(row[reviewedCol]),
          ideasReviewed: toNumber(row[selectedCol]),
          selectionPercent: toNumber(row[selectionPercentCol]),
        };
        break;
      }

      ReviewedAndSelectedData.push({
        id: ReviewedAndSelectedData.length + 1,
        stage: meetingLabel,
        ideas: toNumber(row[reviewedCol]),
        ideasReviewed: toNumber(row[selectedCol]),
        selectionPercent: toNumber(row[selectionPercentCol]),
      });
    }
  }

  // "14 Key Parameters of Benchmarking" table — optional; stays empty until this section
  // exists in the sheet (look for a "#"/"Parameter"/"Ideas Completed"-style header row).
  const KeyParametersModel = [];
  const paramHeaderIdx = findRowIndex(rows, (r) => {
    const text = r.map((c) => normalizeLabel(String(c ?? ""))).join("|");
    return text.includes("parameter") && (text.includes("ideas completed") || text.includes("ideas"));
  });

  if (paramHeaderIdx !== -1) {
    const headerRow = rows[paramHeaderIdx];
    const cols = {
      no: headerRow.findIndex((c) => normalizeLabel(String(c ?? "")) === "#"),
      parameter: headerRow.findIndex((c) => normalizeLabel(String(c ?? "")).startsWith("parameter")),
      ideas: headerRow.findIndex((c) => normalizeLabel(String(c ?? "")).includes("ideas")),
    };

    for (let i = paramHeaderIdx + 1; i < rows.length; i += 1) {
      const row = rows[i];
      const parameterName = String(cleanCell(row[cols.parameter]) || "").trim();
      if (!parameterName) break;
      if (normalizeLabel(parameterName).startsWith("total")) break;

      const no = cols.no !== -1 ? toNumber(row[cols.no]) : KeyParametersModel.length + 1;
      KeyParametersModel.push({
        no,
        parameters: parameterName,
        ideas: toNumber(row[cols.ideas]),
      });
    }
  }

  bestSchoolSummary.ideasTarget = ideasTarget;

  // The sheet dropped its explicit "Idea Reviewed"/"Ideas Selected"/"Completion %" summary rows
  // at some point (readTopLevelValue now finds nothing for them), but the same figures already
  // exist as the Total rows of the two tables below — read those instead of showing 0, rather
  // than inventing a new calculation. "Ideas Generated" and "Parameters" have no such fallback:
  // the sheet's only remaining text for the former is a free-text cell ("Total Ideas collected -
  // 16000+"), and no per-parameter breakdown exists anywhere in the sheet at all (see
  // KeyParametersModel above), so those two stay whatever readTopLevelValue found (0 if nothing).
  if (!bestSchoolSummary.ideaReviewed && ReviewedAndSelectedTotal) {
    bestSchoolSummary.ideaReviewed = ReviewedAndSelectedTotal.ideas;
  }
  if (!bestSchoolSummary.ideasSelected && ReviewedAndSelectedTotal) {
    bestSchoolSummary.ideasSelected = ReviewedAndSelectedTotal.ideasReviewed;
  }
  if (!bestSchoolSummary.completionPercent && IdeasGenerationTargetTotal?.overallTargetPercent) {
    bestSchoolSummary.completionPercent = `${IdeasGenerationTargetTotal.overallTargetPercent}%`;
  }

  return {
    GenerationTimelineData,
    IdeasGenerationTargetData,
    IdeasGenerationTargetTotal,
    KeyParametersModel,
    ReviewedAndSelectedData,
    ReviewedAndSelectedTotal,
    bestSchoolSummary,
  };
};

// ---------------------------------------------------------------------------
// ACADEMICS tab (Grade 10 consolidated exam analysis)
// ---------------------------------------------------------------------------

const parseAcademicsSheet = (rows) => {
  // The sheet's own descriptive title row (e.g. "GRADE 10 — UNIT TEST II (JUNE 2026) —
  // CONSOLIDATED ANALYSIS (KVR Campus + Perumbakkam Campus)") — read it verbatim rather than
  // hardcoding a title, so it stays correct as the month/campuses/exam name change.
  const titleRow = rows.find((r) => /consolidated analysis/i.test(rowLabel(r)));
  const academicsTitle = titleRow ? rowLabel(titleRow) : "";

  const keyMetricHeaderIdx = findRowIndex(rows, (r) => rowLabel(r) === "KEY METRIC");
  const summary = {};
  if (keyMetricHeaderIdx !== -1) {
    for (let i = keyMetricHeaderIdx + 1; i < rows.length; i += 1) {
      const { label, value } = getLabelValue(rows[i]);
      if (!label) break;
      summary[label] = value;
    }
  }

  // "PASS MARK ASSUMPTION (%)" sits above the "KEY METRIC" block as its own standalone row,
  // so it has to be found independently rather than by scanning after the KEY METRIC header.
  const passMarkRow = rows.find((r) => /pass mark assumption/i.test(rowLabel(r)));
  if (passMarkRow) {
    const { label, value } = getLabelValue(passMarkRow);
    summary[label] = value;
  }

  const sectionHeaderIdx = findRowIndex(rows, (r) => rowLabel(r) === "Section / Campus");
  const academicsSectionData = [];
  if (sectionHeaderIdx !== -1) {
    const headerRow = rows[sectionHeaderIdx];
    const cols = {
      section: headerColIndex(headerRow, "Section / Campus"),
      students: headerColIndex(headerRow, "No. of Students"),
      average: headerColIndex(headerRow, "Average % (student-weighted)"),
      highest: headerColIndex(headerRow, "Highest %"),
      lowest: headerColIndex(headerRow, "Lowest %"),
      pass: headerColIndex(headerRow, "Pass %"),
      belowPass: headerColIndex(headerRow, "Students Below Pass Mark"),
    };

    for (let i = sectionHeaderIdx + 1; i < rows.length; i += 1) {
      const row = rows[i];
      const section = String(cleanCell(row[cols.section]) || "").trim();
      if (!section) break;
      if (/^note:/i.test(section)) break;

      academicsSectionData.push({
        section,
        students: toNumber(row[cols.students]),
        average: toNumber(row[cols.average]),
        highest: toNumber(row[cols.highest]),
        lowest: toNumber(row[cols.lowest]),
        pass: toNumber(row[cols.pass]),
        belowPass: toNumber(row[cols.belowPass]),
        isCombined: /combined \(all campuses\)/i.test(section),
      });
    }
  }

  return {
    academicsTitle,
    academicsSectionData,
    academicsSummary: {
      passMarkAssumption: toNumber(summary["PASS MARK ASSUMPTION (%)"]),
      totalStudents: toNumber(summary["Total students (Grade 10, all sections)"]),
      overallAverage: toNumber(summary["Overall Grade Average (%) — student-weighted"]),
      highest: toNumber(summary["Highest Overall % (student)"]),
      lowest: toNumber(summary["Lowest Overall % (student)"]),
      atAbovePassMark: toNumber(summary["Students at/above Pass Mark"]),
      belowPassMark: toNumber(summary["Students below Pass Mark"]),
      overallPassPercent: toNumber(summary["Overall Pass % (Grade)"]),
    },
  };
};

// ---------------------------------------------------------------------------
// TOPPERS & SUPPORT NEEDED tab (Grade 10 top/bottom 10 by Overall %)
// ---------------------------------------------------------------------------

// Both blocks share the same "Rank,Name,Section,Overall %,Roll No" header shape; only the
// section-title text that precedes each block differs, so one reader parses either.
const parseRankedStudentBlock = (rows, sectionTitleMatch) => {
  const sectionIdx = findRowIndex(rows, (r) => sectionTitleMatch.test(rowLabel(r)));
  const headerIdx = sectionIdx === -1
    ? -1
    : findRowIndex(rows, (r) => rowLabel(r) === "Rank", sectionIdx + 1);

  if (headerIdx === -1) return [];

  const headerRow = rows[headerIdx];
  const cols = {
    rank: headerColIndex(headerRow, "Rank"),
    name: headerColIndex(headerRow, "Name"),
    section: headerColIndex(headerRow, "Section"),
    overall: headerColIndex(headerRow, "Overall %"),
    rollNo: headerColIndex(headerRow, "Roll No"),
  };

  const students = [];
  for (let i = headerIdx + 1; i < rows.length; i += 1) {
    const row = rows[i];
    const rank = String(cleanCell(row[cols.rank]) || "").trim();
    if (!rank) break;

    students.push({
      rank: toNumber(rank),
      name: String(cleanCell(row[cols.name]) || "").trim(),
      section: String(cleanCell(row[cols.section]) || "").trim(),
      overall: toNumber(row[cols.overall]),
      rollNo: String(cleanCell(row[cols.rollNo]) || "").trim(),
    });
  }

  return students;
};

const parseToppersSheet = (rows) => {
  // The sheet now carries two header lines that both say "toppers ... needing support": row 1 is
  // the tab's own shouting-caps section name ("GRADE 10 TOPPERS & STUDENTS NEEDING SUPPORT"), row
  // 2 is a Title Case restatement clearly meant as the display heading, and row 3 is the actual
  // "Ranked by ..." description. Prefer the SECOND match (row 2) as the heading and the row right
  // after it (row 3) as the subtitle; if the sheet ever reverts to only one such line, fall back
  // to that single row as the heading so this doesn't go blank.
  const titleRowIndexes = [];
  rows.forEach((row, index) => {
    if (/toppers.*needing support/i.test(rowLabel(row))) titleRowIndexes.push(index);
  });
  const titleIdx = titleRowIndexes.length > 1 ? titleRowIndexes[1] : titleRowIndexes[0];

  const toppersTitle = titleIdx !== undefined ? rowLabel(rows[titleIdx]) : "";
  const toppersSubtitle = titleIdx !== undefined ? rowLabel(rows[titleIdx + 1] || []) : "";

  // Matched on "BOTTOM 10", not the fuller "students needing support" phrase — that phrase is
  // also a substring of the sheet's own overall title ("GRADE 10 TOPPERS & STUDENTS NEEDING
  // SUPPORT"), which sits above both blocks and would otherwise match first.
  const topPerformers = parseRankedStudentBlock(rows, /top\s*10\s*performers/i);
  const studentsNeedingSupport = parseRankedStudentBlock(rows, /bottom\s*10/i);

  return { toppersTitle, toppersSubtitle, topPerformers, studentsNeedingSupport };
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const fetchDashboardDataFromSheet = async () => {
  if (!PUB_ID) {
    throw new Error("No Google Sheet publish URL configured (REACT_APP_GOOGLE_SHEET_CSV_URL is missing or invalid).");
  }

  const tabs = await fetchAllTabs();

  const currentMonth = parseMonthSheet(tabs.month || []);
  const mainDashboard = parseMainDashboardSheet(tabs.mainDashboard || []);
  const perumbakkamModelData = parseCampusSheet(tabs.perumbakkam || []);
  const kvrModelData = parseCampusSheet(tabs.kvr || []);
  const thiruvanmiyurModelData = parseCampusSheet(tabs.thiruvanmiyur || []);
  const thirumudivakkamModelData = parseCampusSheet(tabs.thirumudivakkam || []);
  const hyderabadModelData = parseCampusSheet(tabs.hyderabad || []);
  const transportRows = parseGroupComparisonSheet(tabs.transport || []);
  const hrRows = parseGroupComparisonSheet(tabs.humanResources || []);
  const bestSchool = parseBestSchoolSheet(tabs.bestSchool || []);
  const academics = parseAcademicsSheet(tabs.academics || []);
  const toppers = parseToppersSheet(tabs.toppers || []);

  return {
    currentMonth,
    ...mainDashboard,
    perumbakkamModelData,
    kvrModelData,
    KVRCampusModelData: kvrModelData, // alias: Kvr.jsx reads this exact key
    thiruvanmiyurModelData,
    thirumudivakkamModelData,
    hyderabadModelData,
    transportRows,
    hrRows,
    hrGroupTotals: hrRows.groupTotals || {},
    ...bestSchool,
    ...academics,
    ...toppers,
  };
};
